import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import NewAppointmentModal from '../../CreateAppointment/Components/NewAppointmentModal';
import MessageModal from '../../../../shared/components/Modals/MessageModal';
import { createAppointment } from '../../../../redux/actions/appointmentAction';
import { DEFAULT_TIMESTAMP_FORMAT } from '../../../../constants/constants';

const DUPLICATE_APPOINTMENT_SAME_DAY = 'DUPLICATE_APPOINTMENT_SAME_DAY';

function isDuplicateAppointmentSameDayError(error) {
  const text = [
    error?.data?.errorMessage,
    error?.data?.error,
    error?.errorMessage,
    error?.message,
    error?.response?.data?.errorMessage,
    error?.response?.data?.error,
  ]
    .filter(Boolean)
    .join(' ');
  return text.includes(DUPLICATE_APPOINTMENT_SAME_DAY);
}

function duplicateAppointmentSameDayMessage(petName) {
  const name = String(petName || '').trim() || 'This pet';
  return `${name} already has an appointment at your facility on this day.`;
}

const defaultState = {
  shareUpdates: null,
  appointmentDate: null,
  appointmentTime: null,
  time: null,
  appointmentType: null,
  endDate: null,
  isDaycare: false,
  doctor: null,
  referringDoctor: null,
};

function NewPetAppointment({
  pet,
  isOpen,
  createPetAppointment,
  handleClose,
  appointmentCreateError,
  appointmentRequestsExist,
  openPetAppointmentRequestsModal,
}) {
  const params = useParams();
  const [formErrors, setFormErrors] = useState({});
  const [appointment, setAppointment] = useState(defaultState);
  const [daycareVisitsCreated, setDaycareVisitsCreated] = useState(null);

  const [responseBox, setResponseBox] = useState({
    showResponse: false,
    responseType: '',
    responseAlertType: '',
    responseFooter: '',
    message: '',
    onSuccess: null,
    appointment: null,
  });

useEffect(() => {
  if (!appointmentCreateError) return;

  if (isDuplicateAppointmentSameDayError(appointmentCreateError)) {
    setResponseBox({
      showResponse: true,
      message: duplicateAppointmentSameDayMessage(pet?.name),
      responseType: 'alert',
      responseAlertType: 'danger',
      responseFooter: false,
    });
    return;
  }

  const {
    statusText,
    status,
    data: { errorMessage },
  } = appointmentCreateError;

  setResponseBox({
    showResponse: true,
    message: `${statusText}(${status}) - ${errorMessage}`,
    responseType: 'alert',
    responseAlertType: 'danger',
    responseFooter: false,
  });
}, [appointmentCreateError, pet?.name]);

  const onCloseAppointmentModal = (type = 'close-modal') => {
    handleClose();
    setAppointment(defaultState);

    if (type !== 'appointment-created' && appointmentRequestsExist) {
      openPetAppointmentRequestsModal(pet._id);
    }
  };

const createAppointment = async () => {
  const {
    shareUpdates,
    appointmentDate,
    appointmentTime,
    appointmentType: type,
    doctor,
    referringDoctor,
    appointmentTypeOther: typeOther,
    endDate,
    isDaycare,
  } = appointment;

  if (!type || !appointmentDate) {
    return setFormErrors({
      appointmentType: type ? null : 'Appointment type is a required field',
      appointmentDate: appointmentDate ? null : 'Date is a required field',
    });
  }

  if (isDaycare && !endDate) {
    return setFormErrors({
      endDate: 'End Date is a required field',
    });
  }

  if (
    isDaycare &&
    moment(endDate).startOf('day').isBefore(moment(appointmentDate).startOf('day'))
  ) {
    return setFormErrors({
      endDate: 'End Date cannot be before Stay Date',
    });
  }

  const start = moment(appointmentDate).startOf('day');
  const end = moment(isDaycare ? endDate : appointmentDate).startOf('day');
  const dates = [];
  const current = start.clone();

  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.clone());
    current.add(1, 'day');
  }

  const basePayload = {
    parentId: params.parentId,
    pet: pet._id,
    time: appointmentTime.format('hh:mm A'),
    type,
    typeOther: typeOther?.trim(),
    doctor: doctor === '' ? null : doctor,
    referringDoctor: referringDoctor === '' ? null : referringDoctor,
    shareUpdates: shareUpdates ?? false,
  };

  try {
    for (const date of dates) {
      await createPetAppointment({
        ...basePayload,
        appointmentDate: date.format(DEFAULT_TIMESTAMP_FORMAT),
      });
    }

if (isDaycare) {
  setAppointment((prev) => ({
    ...prev,
    appointmentDate: null,
    endDate: null,
  }));
  setFormErrors({});
  setDaycareVisitsCreated(dates.length);
  setTimeout(() => setDaycareVisitsCreated(null), 2000);
  return;
}

    onClose();
    onCloseAppointmentModal('appointment-created');

    setResponseBox({
      showResponse: true,
      message: `Appointment is successfully created.`,
      responseType: 'ok',
      responseFooter: false,
    });

    setTimeout(() => {
      setResponseBox((prev) => ({
        ...prev,
        showResponse: false,
      }));
    }, 3000);
  } catch (error) {
    // Error modal comes from appointmentCreateError useEffect
  }
};

  const onChangeAppointmentType = (event) => {
  const value = event.currentTarget.value;
  const label = (event.currentTarget.selectedOptions[0]?.text || '').trim();
  const isDaycare = label === 'Daycare';

  setAppointment((prev) => ({
    ...prev,
    appointmentType: value,
    isDaycare,
    endDate: isDaycare ? prev.appointmentDate || null : null,
  }));
  setFormErrors({ appointmentType: '' });
};

const onChangeSelect = (e, type, resetShareUpdates = false) => {
  setAppointment((prev) => ({
    ...prev,
    [type]: e.value,
    shareUpdates:
      type === 'referringDoctor' ? !resetShareUpdates : prev.shareUpdates,
  }));
};

  const handleShareUpdates = () => {
    setAppointment((prev) => ({
      ...prev,
      shareUpdates: !prev.shareUpdates,
    }));
  };

  const handledateChange = (appointmentDate) => {
  setAppointment((prev) => ({
    ...prev,
    appointmentDate,
    endDate: appointmentDate || null,
  }));
};

  const handleEndDateChange = (endDate) => {
  setAppointment((prev) => ({
    ...prev,
    endDate,
  }));
};

  const handleTimeChange = (time) => {
    if (time) {
      const appointmentTime = moment(time);

      setAppointment((prev) => ({
        ...prev,
        appointmentTime,
      }));
    }
  };

  const onClose = () => {
    setResponseBox({
      shareUpdates: null,
      appointmentDate: null,
      appointmentTime: null,
      time: null,
      appointmentType: null,
      doctor: null,
      referringDoctor: null,
    });
  };

  return (
    <>
      <MessageModal
        show={responseBox.showResponse}
        type={responseBox.responseType}
        alertType={responseBox.responseAlertType}
        footer={responseBox.responseFooter}
        onSuccess={responseBox.onSuccess}
        onClose={onClose}
        appointment={responseBox.appointment}
        message={responseBox.message}
      />

      <NewAppointmentModal
        isOpen={isOpen}
        petId={pet?._id}
        reschedule={false}
        createData={{
          shareUpdates: appointment.shareUpdates,
          appointmentDate: appointment.appointmentDate,
          appointmentTime: appointment.appointmentTime,
          time: appointment.time,
          appointmentType: appointment.appointmentType,
          endDate: appointment.endDate,
          isDaycare: appointment.isDaycare,
          doctor: appointment.doctor,
          referringDoctor: appointment.referringDoctor,
          errors: formErrors,
          collapse: isOpen,
        }}
        handledateChange={handledateChange}
        handleEndDateChange={handleEndDateChange}
        handleTimeChange={handleTimeChange}
        onChangeSelect={onChangeSelect}
        handleShareUpdates={handleShareUpdates}
        appointmentDate={appointment.appointmentDate}
        appointmentTime={appointment.appointmentTime}
        viewMode
        onClose={onCloseAppointmentModal}
        handleChange={onChangeAppointmentType}
        submit={createAppointment}
        daycareVisitsCreated={daycareVisitsCreated}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  ...state,
  appointmentCreated: state.appointments.createAppointment,
  appointmentCreateError: state.appointments.createAppointmentError,
});

const mapDispatchToProps = {
  createPetAppointment: createAppointment,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewPetAppointment);
